import { useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Click a name, type a new one, Enter.
 *
 * Extracted because the speaker heading and the recording title want the exact
 * same behaviour, and the parts that are easy to get subtly different are the
 * parts that matter: Escape must restore the original rather than commit
 * whatever was half-typed, blur must commit rather than silently discard, and
 * renaming something to what it already said must not fire a request.
 */
export function InlineRename({
  value,
  onRename,
  label,
  className,
  inputClassName,
  disabled,
}: {
  value: string;
  /** Omit to render plain text — that is what makes this safe to use read-only. */
  onRename?: (next: string) => void;
  /** For screen readers, e.g. "Rename recording". */
  label: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  /**
   * Speaker names and recording titles are content, not chrome — they are
   * whatever language the meeting was in. Without a base direction of their
   * own, an Arabic name containing a Latin word renders its parts in the wrong
   * order, and the editing input puts the caret on the wrong side.
   */
  if (!onRename) {
    return (
      <span dir="auto" className={className}>
        {value}
      </span>
    );
  }

  const commit = () => {
    const next = draft.trim();
    // An empty title would leave the recording with nothing to identify it in
    // the library, and an unchanged one is not an edit.
    if (next && next !== value) onRename(next);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        autoFocus
        dir="auto"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(value);
            setEditing(false);
          }
        }}
        aria-label={label}
        className={cn(
          "min-w-0 rounded border border-border bg-surface px-1.5 py-0.5",
          "focus:outline-none focus:ring-1 focus:ring-accent",
          className,
          inputClassName,
        )}
      />
    );
  }

  return (
    <button
      type="button"
      dir="auto"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      title={label}
      className={cn(
        "rounded text-start transition-colors",
        "hover:bg-surface-hover hover:underline hover:decoration-dotted",
        className,
      )}
    >
      {value}
    </button>
  );
}
